import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactPlayer from 'react-player';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, CheckCircle, Circle, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0); // Current playback position in seconds
  const [videoError, setVideoError] = useState(null);
  
  const playerRef = useRef(null);
  const lastSaveTimeRef = useRef(0); // Track last save timestamp
  const autoSaveIntervalRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCourseData();
  }, [courseId, token, navigate]);

  // Auto-save video progress every 15 seconds
  useEffect(() => {
    if (!course || !enrollment || !playing) return;
    
    const currentLesson = course.lessons[currentLessonIndex];
    if (!currentLesson) return;
    
    // Set up interval for auto-save
    autoSaveIntervalRef.current = setInterval(() => {
      if (videoProgress > 0) {
        saveVideoProgress(currentLesson.id, videoProgress);
      }
    }, 15000); // 15 seconds
    
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [course, enrollment, playing, currentLessonIndex, videoProgress]);

  // Load saved progress when lesson changes
  useEffect(() => {
    if (!course || !enrollment) return;
    
    const currentLesson = course.lessons[currentLessonIndex];
    if (!currentLesson) return;
    
    // Reset error when switching lessons
    setVideoError(null);
    setPlaying(false);
    
    const lessonProgress = enrollment.lesson_progress || {};
    if (lessonProgress[currentLesson.id]) {
      const savedTimestamp = lessonProgress[currentLesson.id].last_timestamp;
      if (savedTimestamp > 10) {
        setVideoProgress(savedTimestamp);
        if (playerRef.current) {
          playerRef.current.seekTo(savedTimestamp, 'seconds');
        }
      }
    } else {
      setVideoProgress(0);
    }
  }, [currentLessonIndex, course, enrollment]);

  const fetchCourseData = async () => {
    try {
      const enrollmentRes = await axios.get(
        `${API}/courses/enrollment/${courseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!enrollmentRes.data.enrollment) {
        toast.error('You are not enrolled in this course');
        navigate('/courses');
        return;
      }

      setEnrollment(enrollmentRes.data.enrollment);
      
      const courseRes = await axios.get(`${API}/courses/${courseId}`);
      setCourse(courseRes.data.course);
      
      // Get saved progress for current lesson
      const lessonProgress = enrollmentRes.data.enrollment.lesson_progress || {};
      const currentLessonId = courseRes.data.course.lessons?.[currentLessonIndex]?.id;
      
      if (currentLessonId && lessonProgress[currentLessonId]) {
        const savedTimestamp = lessonProgress[currentLessonId].last_timestamp;
        if (savedTimestamp > 10) { // Only resume if more than 10 seconds
          setVideoProgress(savedTimestamp);
          if (playerRef.current) {
            playerRef.current.seekTo(savedTimestamp, 'seconds');
          }
          toast.info(`Resuming from ${Math.floor(savedTimestamp / 60)}:${String(Math.floor(savedTimestamp % 60)).padStart(2, '0')}`);
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
      navigate('/my-courses');
    } finally {
      setLoading(false);
    }
  };

  const saveVideoProgress = async (lessonId, timestamp) => {
    if (!enrollment?.id) return;
    
    try {
      await axios.put(
        `${API}/courses/enrollments/${enrollment.id}/lesson-progress`,
        {
          lesson_id: lessonId,
          video_timestamp: Math.floor(timestamp)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      lastSaveTimeRef.current = Date.now();
    } catch (error) {
      console.error('Error saving video progress:', error);
      // Silent fail - don't show error to user for auto-save
    }
  };

  const handleMarkComplete = async (lessonId) => {
    try {
      await axios.post(
        `${API}/courses/${courseId}/lesson/${lessonId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lesson marked as complete!');
      fetchCourseData();
    } catch (error) {
      console.error('Error marking lesson complete:', error);
      toast.error('Failed to mark lesson as complete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading course...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!course || !course.lessons || course.lessons.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600">No lessons available for this course yet.</p>
            <Button onClick={() => navigate('/my-courses')} className="mt-4">
              Back to My Courses
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentLesson = course.lessons[currentLessonIndex];
  const completedLessons = enrollment?.completed_lessons || [];
  const progress = course.lessons.length > 0 ? (completedLessons.length / course.lessons.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/my-courses')}
          className="mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to My Courses
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <div className="relative aspect-video bg-black">
                  <ReactPlayer
                    ref={playerRef}
                    url={currentLesson.video_url}
                    className="react-player"
                    width="100%"
                    height="100%"
                    playing={playing}
                    controls={true}
                    onReady={() => {
                      console.log('Video ready:', currentLesson.video_url);
                      setVideoError(null);
                    }}
                    onPlay={() => setPlaying(true)}
                    onPause={() => {
                      setPlaying(false);
                      // Save progress when user pauses
                      if (videoProgress > 0) {
                        saveVideoProgress(currentLesson.id, videoProgress);
                      }
                    }}
                    onProgress={(state) => {
                      // Update current playback position
                      setVideoProgress(state.playedSeconds);
                    }}
                    onEnded={() => {
                      setPlaying(false);
                      // Save progress when video ends
                      saveVideoProgress(currentLesson.id, videoProgress);
                    }}
                    onError={(error) => {
                      console.error('Video error:', error);
                      setVideoError('Unable to load video. Please check the video URL or try a different video.');
                      toast.error('Video failed to load. Please check the video URL.');
                    }}
                    config={{
                      youtube: {
                        playerVars: { 
                          showinfo: 1,
                          modestbranding: 1,
                          rel: 0
                        }
                      },
                      file: {
                        attributes: {
                          controlsList: 'nodownload'
                        }
                      }
                    }}
                  />
                  {videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
                      <div className="text-center text-white p-6">
                        <p className="text-lg mb-2">⚠️ Video Load Error</p>
                        <p className="text-sm opacity-75">{videoError}</p>
                        <p className="text-xs mt-2 opacity-50">Current URL: {currentLesson.video_url}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {currentLesson.title}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Lesson {currentLessonIndex + 1} of {course.lessons.length}
                  </p>

                  {currentLesson.description && (
                    <p className="text-gray-600 mb-4">{currentLesson.description}</p>
                  )}

                  {!completedLessons.includes(currentLesson.id) && (
                    <Button onClick={() => handleMarkComplete(currentLesson.id)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Complete
                    </Button>
                  )}

                  {progress >= 100 && (
                    <Button
                      className="w-full mt-4"
                      onClick={async () => {
                        try {
                          toast.info('Generating certificate...');
                          
                          const response = await axios.post(
                            `${API}/certificates/${courseId}/generate`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );

                          // Download certificate
                          const pdfBase64 = response.data.pdf_base64;
                          const byteCharacters = atob(pdfBase64);
                          const byteNumbers = new Array(byteCharacters.length);
                          for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                          }
                          const byteArray = new Uint8Array(byteNumbers);
                          const blob = new Blob([byteArray], { type: 'application/pdf' });
                          
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `certificate-${response.data.certificate_id}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);

                          toast.success('Certificate downloaded successfully!');
                          fetchCourseData(); // Refresh data
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to generate certificate');
                        }
                      }}
                    >
                      <Award className="h-4 w-4 mr-2" />
                      {enrollment?.certificate_id ? 'Download Certificate' : 'Get Certificate'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-4">Course Lessons</h3>
                <div className="space-y-2">
                  {course.lessons.map((lesson, index) => {
                    const isCompleted = completedLessons.includes(lesson.id);
                    const isCurrent = index === currentLessonIndex;

                    return (
                      <div
                        key={lesson.id}
                        onClick={() => setCurrentLessonIndex(index)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          isCurrent
                            ? 'bg-indigo-50 border-2 border-indigo-500'
                            : 'hover:bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            {isCompleted ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{lesson.title}</p>
                            <p className="text-xs text-gray-500">{lesson.duration_minutes} min</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;